import { Link } from "react-router-dom";
import { StyledTagLink } from "../atoms.styles";
import { StyledThDiv, StyledTh, StyledThResizer, StyledTd } from "./Table.styles";

/**
 * A styled `<th>` component used as a table header cell.
 *
 * Params
 * - children (ReactNode): The content of the header cell.
 * - ...props (any): Additional props passed to the StyledTh component.
 *
 * Returns
 * - JSX.Element: A styled table header cell.
 */
export const Th = ({ children, ...props }) => {
  return <StyledTh {...props}>{children}</StyledTh>;
};

/**
 * A styled `<div>` inside `<th>` used for layout or interactive UI (e.g., resizable column).
 *
 * Params
 * - children (ReactNode): Content rendered inside the wrapper div.
 * - ...props (any): Additional props passed to the StyledThDiv component.
 *
 * Returns
 * - JSX.Element: A styled div for flexible layout inside a header cell.
 */
export const ThDiv = ({ children, ...props }) => {
  return <StyledThDiv {...props}>{children}</StyledThDiv>;
};

/**
 * A vertical resizer handle for resizable table columns.
 *
 * Params
 * - props (any): Props applied to the resizer handle, including event handlers or styles.
 *
 * Returns
 * - JSX.Element: A styled resizer separator with accessibility roles and focus handling.
 */
export const ThResizer = props => {
  return <StyledThResizer {...props} aria-orientation="vertical" role="separator" tabIndex={-1} />;
};

/**
 * A styled `<td>` component used as a table data cell.
 *
 * Params
 * - children (ReactNode): The content of the data cell.
 * - ...props (any): Additional props passed to the StyledTd component.
 *
 * Returns
 * - JSX.Element: A styled table data cell.
 */
export const Td = ({ children, ...props }) => {
  return <StyledTd {...props}>{children}</StyledTd>;
};

/**
 * A tagged document link used inside a table cell.
 *
 * Params
 * - children (ReactNode): The tag (or other) content of the link.
 * - ...props (any): Additional props passed to the styled link (`to`, `onClick`).
 *
 * Returns
 * - JSX.Element: A styled react-router link.
 */
export const TagLink = ({ children, ...props }) => {
  return (
    <StyledTagLink as={Link} {...props}>
      {children}
    </StyledTagLink>
  );
};
